import NavLinkAdapter from '@/@fuse/core/NavLinkAdapter/NavLinkAdapter';
import FuseUtils from '@/@fuse/utils';
import Collapse from '@material-ui/core/Collapse';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import FuseNavBadge from '../FuseNavBadge';
import FuseNavItem from '../FuseNavItem';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: 0,
		'&.open': {
			backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,.015)' : 'rgba(0,0,0,.04)',
		},
	},
	item: (props) => ({
		height: 40,
		width: 'calc(100% - 16px)',
		borderRadius: '0 20px 20px 0',
		paddingRight: 12,
		paddingLeft: props.itemPadding > 80 ? 80 : props.itemPadding,
		color: theme.palette.text.primary,
		'&.active > .list-item-text > span': {
			fontWeight: 600,
		},
		'& .list-item-icon': {
			marginRight: 16,
		},
	}),
}));

function needsToBeOpened(location, item) {
	return location && isUrlInChildren(item, location.pathname);
}

function isUrlInChildren(parent, url) {
	if (!parent.children) {
		return false;
	}

	for (let i = 0; i < parent.children.length; i += 1) {
		if (parent.children[i].children) {
			if (isUrlInChildren(parent.children[i], url)) {
				return true;
			}
		}

		if (parent.children[i].url === url || url.includes(parent.children[i].url)) {
			return true;
		}
	}

	return false;
}

const FuseNavVerticalCollapse = ({ item, nestedLevel }) => {
	const location = useLocation();
	const userRole = useSelector(({ auth }) => auth.user.role);
	const [open, setOpen] = useState(() => needsToBeOpened(location, item));
	const classes = useStyles({
		itemPadding: nestedLevel > 0 ? 40 + nestedLevel * 16 : 24,
	});
	const { t } = useTranslation('navigation');

	useEffect(() => {
		if (needsToBeOpened(location, item)) {
			setOpen(true);
		}
	}, [location, item]);

	function handleClick() {
		setOpen(!open);
	}

	if (!FuseUtils.hasPermission(item.auth, userRole)) {
		return null;
	}

	return (
		<ul className={clsx(classes.root, open && 'open')}>
			<ListItem
				button
				className={clsx(classes.item, 'list-item')}
				component={item.url ? NavLinkAdapter : 'li'}
				onClick={handleClick}
				role="button"
				to={item.url}
			>
				{item.icon ? (
					<Icon className="list-item-icon text-16 shrink-0" color="action">
						{item.icon}
					</Icon>
				) : null}

				<ListItemText
					className="list-item-text"
					classes={{ primary: 'text-14' }}
					primary={item.translate ? t(item.translate) : item.title}
				/>

				{item.badge ? <FuseNavBadge badge={item.badge} className="mx-4" /> : null}

				<IconButton
					className="w-40 h-40 -mx-12 p-0 focus:bg-transparent hover:bg-transparent"
					disableRipple
					onClick={(ev) => ev.preventDefault()}
				>
					<Icon className="text-16 arrow-icon" color="inherit">
						{open ? 'expand_less' : 'expand_more'}
					</Icon>
				</IconButton>
			</ListItem>

			{item.children ? (
				<Collapse className="collapse-children" in={open}>
					{item.children.map((_item) => (
						<FuseNavItem item={_item} key={_item.id} nestedLevel={nestedLevel + 1} type={`vertical-${_item.type}`} />
					))}
				</Collapse>
			) : null}
		</ul>
	);
};

FuseNavVerticalCollapse.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		icon: PropTypes.string,
		children: PropTypes.array,
	}),
};

const NavVerticalCollapse = React.memo(FuseNavVerticalCollapse);

export default NavVerticalCollapse;
